import React, { useEffect, useState, useCallback } from "react";
import { ListChecks, Loader2, Check, EyeOff, Clock, ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import PageHeader, { EmptyState, fmtDate } from "@/components/PageHeader";
import { useFeatureFlags, FeatureDisabled } from "@/lib/featureFlags";

function fmtDeadline(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, { timeZone: "America/Chicago", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

// Countdown label + urgency once within 24h of the deadline. null = not near yet / already closed.
function countdown(iso, nowMs) {
  if (!iso) return null;
  const end = new Date(iso).getTime();
  if (isNaN(end)) return null;
  const ms = end - nowMs;
  if (ms <= 0) return null;
  const hours = ms / 3600000;
  if (hours > 24) return null;
  let label;
  if (ms < 3600000) {
    const mins = Math.max(1, Math.round(ms / 60000));
    label = `Closes in ${mins}m`;
  } else {
    label = `Closes in ${Math.round(hours)}h`;
  }
  return { label, urgent: hours <= 3 };
}

function OptionButton({ option, active, disabled, rounded, onClick, testId }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} data-testid={testId}
      className={`flex w-full items-center gap-3 rounded-lg border p-3 text-left text-sm transition-colors ${active ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"} ${disabled ? "cursor-not-allowed opacity-70" : ""}`}>
      <span className={`flex h-5 w-5 shrink-0 items-center justify-center border ${rounded ? "rounded-full" : "rounded"} ${active ? "border-white" : "border-slate-300"}`}>
        {active && <Check className="h-3.5 w-3.5" />}
      </span>
      <span className="font-medium">{option.label}</span>
    </button>
  );
}

function ChoiceCard({ choice, onSaved, collapsible = false }) {
  const [sel, setSel] = useState(choice.my_option_ids || []);
  const [saving, setSaving] = useState(false);
  const [nowMs, setNowMs] = useState(Date.now());
  const [expanded, setExpanded] = useState(!collapsible);
  useEffect(() => {
    const t = setInterval(() => setNowMs(Date.now()), 30000);
    return () => clearInterval(t);
  }, []);
  const accepting = choice.accepting;
  const isMenu = (choice.categories || []).length > 0;
  const cd = accepting ? countdown(choice.deadline, nowMs) : null;

  const flatMax = choice.max_select ?? Infinity;
  const noneIds = new Set([...(choice.options || []), ...(choice.categories || []).flatMap((c) => c.options || [])].filter((o) => o.none).map((o) => o.id));
  const flatFull = !isMenu && flatMax > 1 && flatMax !== Infinity && sel.filter((x) => !noneIds.has(x)).length >= flatMax;

  // Toggle one pick inside a group limited to `max` (1 = swap like a radio). "None" is exclusive.
  const pick = (group, max, id, name) => {
    const groupIds = group.map((o) => o.id);
    const noneId = group.find((o) => o.none)?.id;
    if (sel.includes(id)) { setSel((p) => p.filter((x) => x !== id)); return; }
    if (id === noneId) { setSel((p) => [...p.filter((x) => !groupIds.includes(x)), id]); return; }
    const base = sel.filter((x) => x !== noneId);
    const inGroup = base.filter((x) => groupIds.includes(x)).length;
    if (inGroup < max) { setSel([...base, id]); return; }
    if (max === 1) { setSel([...base.filter((x) => !groupIds.includes(x)), id]); return; }
    toast.error(name ? `You can choose up to ${max} in "${name}"` : `You can choose up to ${max}`);
  };

  const toggle = (id) => {
    if (!accepting) return;
    if (isMenu) {
      const cat = (choice.categories || []).find((c) => (c.options || []).some((o) => o.id === id));
      if (cat) pick(cat.options, cat.max_select ?? Infinity, id, cat.name);
    } else {
      pick(choice.options, flatMax, id);
    }
  };

  const submit = async () => {
    if (sel.length === 0) { toast.error("Select a choice"); return; }
    setSaving(true);
    try {
      await api.post(`/member/choices/${choice.id}/respond`, { option_ids: sel });
      toast.success("Response saved"); onSaved();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Card className="border-slate-200 p-4 shadow-sm" data-testid={`member-choice-${choice.id}`}>
      <div className={`flex items-start justify-between gap-2 ${collapsible ? "cursor-pointer select-none" : ""}`}
        onClick={collapsible ? () => setExpanded((v) => !v) : undefined}
        data-testid={collapsible ? `member-choice-toggle-${choice.id}` : undefined}>
        <div className="min-w-0">
          <p className="font-semibold text-slate-900">{choice.title}</p>
          {expanded && choice.description && <p className="mt-0.5 text-sm text-slate-600">{choice.description}</p>}
          {collapsible && !expanded && (
            <p className="mt-0.5 text-xs text-slate-400">{choice.responded ? "Your response was recorded" : "No response recorded"} · {fmtDate(choice.created_at)}</p>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <span className={`rounded-full px-2 py-0.5 text-xs ${accepting ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}`}>{accepting ? "Open" : "Closed"}</span>
          {collapsible && <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${expanded ? "rotate-180" : ""}`} />}
        </div>
      </div>
      {expanded && (<>
      {cd && (
        <div className="mt-2" data-testid={`member-choice-countdown-${choice.id}`}>
          <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${cd.urgent ? "bg-red-100 text-red-700 animate-pulse" : "bg-amber-100 text-amber-700"}`}>
            <Clock className="h-3 w-3" /> {cd.label}
          </span>
        </div>
      )}
      <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-slate-400">
        <span data-testid={`member-choice-limit-${choice.id}`}>{isMenu ? "Choose from each list below" : flatMax === 1 ? "Choose one" : flatMax === Infinity ? "Choose one or more" : `Choose up to ${flatMax}`}</span>
        {choice.anonymous && <span className="inline-flex items-center gap-1 text-amber-600"><EyeOff className="h-3 w-3" /> Anonymous</span>}
        {choice.deadline && <span>· Closes {fmtDeadline(choice.deadline)}</span>}
        <span>· {fmtDate(choice.created_at)}</span>
      </div>

      {isMenu ? (
        <div className="mt-3 space-y-4" data-testid={`member-choice-menu-${choice.id}`}>
          {choice.categories.map((cat) => {
            const max = cat.max_select == null ? Infinity : cat.max_select;
            const catIds = cat.options.map((o) => o.id);
            const inCat = sel.filter((x) => catIds.includes(x) && !noneIds.has(x)).length;
            const full = inCat >= max;
            const hint = max === 1 ? "Choose one" : (max === Infinity ? "Choose any" : `Up to ${max}`);
            return (
              <div key={cat.id}>
                <div className="mb-1.5 flex items-center justify-between">
                  <p className="text-xs font-bold uppercase tracking-wide text-slate-500" data-testid={`member-choice-cat-${cat.id}`}>{cat.name}</p>
                  <span className="text-[11px] font-medium text-slate-400" data-testid={`member-choice-cat-limit-${cat.id}`}>{hint}</span>
                </div>
                <div className="space-y-2">
                  {cat.options.map((o) => {
                    const selected = sel.includes(o.id);
                    const disabled = !accepting || (!o.none && max !== Infinity && max > 1 && full && !selected);
                    return (
                      <OptionButton key={o.id} option={o} active={selected} disabled={disabled} rounded={max === 1}
                        onClick={() => toggle(o.id)} testId={`member-choice-option-${o.id}`} />
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="mt-3 space-y-2" data-testid={`member-choice-options-${choice.id}`}>
          {choice.options.map((o) => (
            <OptionButton key={o.id} option={o} active={sel.includes(o.id)} disabled={!accepting || (flatFull && !o.none && !sel.includes(o.id))} rounded={flatMax === 1}
              onClick={() => toggle(o.id)} testId={`member-choice-option-${o.id}`} />
          ))}
        </div>
      )}

      {accepting ? (
        <Button onClick={submit} disabled={saving} className="mt-4 w-full rounded-full bg-slate-900 hover:bg-slate-800" data-testid={`member-choice-submit-${choice.id}`}>
          {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {choice.responded ? "Update response" : "Submit response"}
        </Button>
      ) : (
        <p className="mt-3 text-center text-xs text-slate-400">{choice.responded ? "Your response was recorded." : "This choice is closed."}</p>
      )}
      </>)}
    </Card>
  );
}

const CLOSED_PAGE_SIZE = 5;

export default function MemberChoices() {
  const { choices: choicesOn } = useFeatureFlags();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [closedPage, setClosedPage] = useState(1);

  const load = useCallback(async () => {
    const { data } = await api.get("/member/choices");
    setItems(data.items || []); setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!choicesOn) return <FeatureDisabled name="Choices" />;

  const openItems = items.filter((c) => c.accepting);
  const closedItems = items.filter((c) => !c.accepting);
  const totalClosedPages = Math.max(1, Math.ceil(closedItems.length / CLOSED_PAGE_SIZE));
  const curClosed = Math.min(closedPage, totalClosedPages);
  const closedRows = closedItems.slice((curClosed - 1) * CLOSED_PAGE_SIZE, curClosed * CLOSED_PAGE_SIZE);

  return (
    <div>
      <PageHeader title="Choices" subtitle="Respond to prompts from your coach." />
      {loading ? (
        <div className="flex items-center justify-center py-16 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : items.length === 0 ? (
        <EmptyState title="No choices yet" description="Your coach hasn't posted any prompts." testId="member-choices-empty" />
      ) : (
        <div className="space-y-6">
          <div>
            <h3 className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">Open prompts</h3>
            {openItems.length === 0 ? (
              <p className="rounded-lg border border-dashed border-slate-200 p-4 text-sm text-slate-400" data-testid="member-choices-no-open">No open prompts right now.</p>
            ) : (
              <div className="grid gap-3 md:grid-cols-2" data-testid="member-choices-list">
                {openItems.map((c) => <ChoiceCard key={c.id} choice={c} onSaved={load} />)}
              </div>
            )}
          </div>

          {closedItems.length > 0 && (
            <div>
              <div className="mb-2 flex items-center justify-between">
                <h3 className="text-xs font-bold uppercase tracking-wide text-slate-500">Ended prompts</h3>
                <span className="text-xs text-slate-400" data-testid="member-closed-page-label">Page {curClosed} of {totalClosedPages}</span>
              </div>
              <div className="space-y-2" data-testid="member-choices-closed-list">
                {closedRows.map((c) => <ChoiceCard key={c.id} choice={c} onSaved={load} collapsible />)}
              </div>
              {totalClosedPages > 1 && (
                <div className="mt-3 flex items-center justify-center gap-2">
                  <Button variant="outline" size="sm" className="rounded-full" disabled={curClosed <= 1} onClick={() => setClosedPage((p) => Math.max(1, p - 1))} data-testid="member-closed-prev"><ChevronLeft className="h-4 w-4" /></Button>
                  <span className="text-sm text-slate-500">{curClosed} / {totalClosedPages}</span>
                  <Button variant="outline" size="sm" className="rounded-full" disabled={curClosed >= totalClosedPages} onClick={() => setClosedPage((p) => Math.min(totalClosedPages, p + 1))} data-testid="member-closed-next"><ChevronRight className="h-4 w-4" /></Button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
